import { Skeleton, useTheme } from "@mui/material";
import { DeleteBin, Wishlist as WishlistIcon, WishlistYes } from "@pmag/icons";
import { useAttributeConfig } from "@src/hooks/useAttributeConfig";
import { useWishlist } from "@src/hooks/useWishlist";
import { type PriceCellItem } from "@src/pages/subGroupDetail/subgroupDetailsListSection/priceCell/PriceCell";
import { getWishlistEntry } from "@src/pages/wishlist/Wishlist.utils";
import { dispatchSPFAddToWishlistEvent, dispatchSPFRemoveFromWishlistEvent } from "@src/utils/events";
import * as React from "react";

import { StyledIconButton } from "./WishlistIconButton.styles";

interface WishlistIconButtonProps {
    brand: string;
    clientWishlist: string[];
    item: PriceCellItem;
    modelId: string;
    showCellInWishlist?: boolean;
}

export const WishlistIconButton: React.FunctionComponent<WishlistIconButtonProps> = ({
    brand,
    modelId,
    item,
    clientWishlist,
    showCellInWishlist = false,
}) => {
    const theme = useTheme();
    const { wishlistEnabled, wishlistClientside } = useAttributeConfig();
    const { wishlist, addToWishList, removeFromWishList } = useWishlist();
    const [loading, setLoading] = React.useState(wishlistClientside);
    const [processing, setProcessing] = React.useState(false);

    const wishlistEntry = getWishlistEntry(item.id, modelId);
    const isOnWishlist = wishlist.includes(wishlistEntry) || (item.article && clientWishlist.includes(item.article.articleNumber)) || false;

    React.useEffect(() => {
        if (loading) {
            setTimeout(() => setLoading(false), 5000);
        }
    }, [loading]);

    React.useEffect(() => {
        setProcessing(false);
    }, [clientWishlist]);

    React.useEffect(() => {
        if (processing) {
            setTimeout(() => setProcessing(false), 5000);
        }
    }, [processing]);

    const onWishlistButtonPressed = () => {
        if (!wishlistClientside) {
            if (wishlist.includes(wishlistEntry)) {
                removeFromWishList(wishlistEntry);
            } else {
                addToWishList(wishlistEntry);
            }
        } else {
            if (item.article) {
                setProcessing(true);
                if (clientWishlist.includes(item.article.articleNumber)) {
                    dispatchSPFRemoveFromWishlistEvent(item.article.articleNumber, brand, item.clientArticleInfo?.clientArticleNumber);
                } else {
                    dispatchSPFAddToWishlistEvent(item.article.articleNumber, brand, item.clientArticleInfo?.clientArticleNumber);
                }
            }
        }
    };

    if (!wishlistEnabled) return null;

    return loading || processing ? (
        <Skeleton height={32} width={32} />
    ) : (
        <StyledIconButton
            onClick={onWishlistButtonPressed}
            isActive={isOnWishlist}
            showInWishList={showCellInWishlist}
            variant={showCellInWishlist ? "secondary" : "primary"}
        >
            {showCellInWishlist ? (
                <DeleteBin size="small" />
            ) : isOnWishlist ? (
                <WishlistYes size="small" color={theme.palette.core.white} />
            ) : (
                <WishlistIcon size="small" color={theme.palette.core.grey._80} />
            )}
        </StyledIconButton>
    );
};
